<script setup lang="ts" generic="SITE_SECTION_NODE">
import { useVirtualizedTreeController, VirtualizedTable } from "@voltalis/ui-kit/components";
import { useTemplateRef } from "vue";

import {
  ISiteSectionsVirtualizedTableExpose,
  ISiteSectionsVirtualizedTableNode,
  ISiteSectionsVirtualizedTableNodeModel,
} from "@/components/site/site-sections-virtualized-table/site-sections-virtualized-table.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import ProgressSpinner from "primevue/progressspinner";

interface ISiteSectionsVirtualizedTableProps {
  siteSectionsForest: ISiteSectionsVirtualizedTableNode<SITE_SECTION_NODE>[];
  /**
   * heights in px
   */
  viewportHeight?: number;
  itemHeight?: number;
  isLoading?: boolean;
}

const props = defineProps<ISiteSectionsVirtualizedTableProps>();
defineSlots<{
  header: void;
  treeNodePadding: void;
  beforeNodeCellToggleIcon(props: { item: ISiteSectionsVirtualizedTableNode<SITE_SECTION_NODE> }): void;
  nodeCellContent(props: { item: ISiteSectionsVirtualizedTableNode<SITE_SECTION_NODE> }): void;
  bodyAdditionalTd(props: { item: ISiteSectionsVirtualizedTableNode<SITE_SECTION_NODE> }): void;
}>();

const { t, k } = useTypedIntl();
const { flattenedTree, toggleNode, refreshFlattenedTree } = await useVirtualizedTreeController<
  SITE_SECTION_NODE,
  ISiteSectionsVirtualizedTableNode<SITE_SECTION_NODE>,
  ISiteSectionsVirtualizedTableNodeModel<SITE_SECTION_NODE>
>(props.siteSectionsForest);

const virtualizedTable = useTemplateRef("virtualizedTable");

async function handleToggleNode(node: ISiteSectionsVirtualizedTableNodeModel<SITE_SECTION_NODE>) {
  await toggleNode(node);
  virtualizedTable.value?.refreshView();
}

function forceUpdate(): void {
  refreshFlattenedTree();
  virtualizedTable.value?.refreshView();
}

defineExpose<ISiteSectionsVirtualizedTableExpose>({
  forceUpdate,
});
</script>

<template>
  <div
    ref="container"
    class="h-full w-full relative site-sections-virtualized-table"
    :data-test-id="DATA_TEST_IDS.components.siteSectionsVirtualizedTable.component"
  >
    <VirtualizedTable
      ref="virtualizedTable"
      :items="flattenedTree"
      :item-height="props.itemHeight"
      :containerHeight="props.viewportHeight"
      :data-test-id="DATA_TEST_IDS.components.siteSectionsVirtualizedTable.table"
    >
      <template #cols>
        <col style="width: 335px" />
      </template>

      <template #header>
        <slot name="header">
          <tr>
            <th>
              <span
                class="uppercase text-text-secondary text-[10px]"
                :data-test-id="DATA_TEST_IDS.components.siteSectionsVirtualizedTable.tableHeader"
              >
                {{ t(k.control.piloting.siteSectionsTree) }}
              </span>
            </th>
          </tr>
        </slot>
      </template>

      <template #item="{ item }">
        <td
          class="virtualized-table-node-cell"
          :data-test-id="DATA_TEST_IDS.components.siteSectionsVirtualizedTable.sectionCell.cell"
        >
          <div class="flex items-center h-full w-full gap-2">
            <template
              v-for="(_parent, _parentIdx) in item.parentsIndexes"
              :key="_parent"
            >
              <slot name="treeNodePadding">
                <div class="tree-node-padding" />
              </slot>
            </template>

            <slot
              :item="item"
              name="beforeNodeCellToggleIcon"
            />

            <button
              v-if="item.children.length"
              class="flex h-full items-center"
              :data-test-id="DATA_TEST_IDS.components.siteSectionsVirtualizedTable.sectionCell.toggleSectionIcon"
              @click="handleToggleNode(item)"
            >
              <i
                :class="['toggle-node-icon pi !text-xs', item.state.isExpanded ? 'pi-angle-down' : 'pi-angle-right']"
              />
            </button>

            <slot
              :item="item"
              name="nodeCellContent"
            >
              <span
                class="tree-node-item"
                :data-test-id="DATA_TEST_IDS.components.siteSectionsVirtualizedTable.sectionCell.content"
              >
                {{ item.name }}
              </span>
            </slot>
          </div>
        </td>
        <slot
          name="bodyAdditionalTd"
          :item="item"
        />
      </template>

      <template #noResultText>
        <span class="text-text-secondary">{{ t(k.global.misc.noResult) }}</span>
      </template>
    </VirtualizedTable>

    <div
      v-if="props.isLoading"
      class="absolute inset-0 bg-white/50 z-50 flex items-center justify-center"
    >
      <ProgressSpinner />
    </div>
  </div>
</template>

<style scoped lang="scss">
.site-sections-virtualized-table {
  .tree-node-padding {
    @apply h-full border-l block;
    margin-left: 1rem;
  }

  .toggle-node-icon {
    &.with-children {
      cursor: pointer;
    }

    &.without-children {
      opacity: 0;
    }
  }
}
</style>
